// JWT de la app (lo emite /getTokenFromAAD o /getTeamsToken tras el
// login con Entra ID y su payload trae { user_id: <correo> }). Vive en
// localStorage porque lo leen las llamadas a /api/*, pero se escribe
// DESPUÉS de que la app montó: el login es asíncrono.
//
// Por eso quien dependa del usuario (p. ej. el selector de modelo del
// SideNav, visible sólo para ciertos correos) no puede leer
// localStorage una sola vez al montar — si lo hace antes de que
// termine el login se queda con "" para siempre y la sección no
// aparece hasta recargar. useAppUserId() se suscribe a la escritura
// del token y re-renderiza cuando llega.

import { useSyncExternalStore } from "react";

const JWT_KEY = "jwt";

const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export const getAppJwt = () => localStorage.getItem(JWT_KEY) || "";

// Único punto de escritura del token: además de guardarlo avisa a los
// componentes suscritos.
export const setAppJwt = (token: string) => {
  localStorage.setItem(JWT_KEY, token || "");
  notify();
};

// El payload del JWT viene en base64url (PyJWT): '-'/'_' en vez de
// '+'/'/' y sin '=' de relleno, que es justo lo que atob no acepta.
const decodeSegment = (segment: string) => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (base64.length % 4)) % 4;
  return atob(base64 + "=".repeat(padding));
};

const decodeUserId = (token: string): string => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return "";
    const payload = JSON.parse(decodeSegment(parts[1]));
    return typeof payload?.user_id === "string" ? payload.user_id : "";
  } catch (error) {
    console.error("[appJwt] Error extracting user_id from JWT:", error);
    return "";
  }
};

// useSyncExternalStore llama a getSnapshot en cada render: el decode se
// cachea contra el token crudo para no repetirlo.
let cachedToken = "";
let cachedUserId = "";

export const getAppUserId = (): string => {
  const token = getAppJwt();
  if (token !== cachedToken) {
    cachedToken = token;
    cachedUserId = decodeUserId(token);
  }
  return cachedUserId;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Otra pestaña que inicia sesión también actualiza este localStorage.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

// Correo del usuario en sesión ("" mientras el login no termina).
export const useAppUserId = () => useSyncExternalStore(subscribe, getAppUserId);
